import { randomBetween, type RandomSource } from "./seeded-random";
import type { SketchPoint } from "./stroke-points";

/** Raw hand-drawn shape generators in content-box pixels: (0, 0) is the
 * content box's top-left. The output feeds `agentInkStroke`. */

const BOX_CORNER_JITTER_PX = 3;
const BOX_EDGE_BOW_PX = 2.5;
const BOX_OVERSHOOT_PX = 30;
const BOX_OVERSHOOT_LIFT_PX = 2;
const BOX_EDGE_SAMPLES = 20;
const ROUNDED_CORNER_PX = 10;
const ROUNDED_CORNER_SAMPLES = 6;

const LOOP_SAMPLES = 120;
const LOOP_TURNS = 1.12;

const HEAD_LENGTH_PX = 14;
const HEAD_ANGLE_RAD = 0.5;

const UNDERLINE_SAMPLES = 50;

const MARKER_REACH_PX = 2;
const MARKER_END_JITTER_PX = 1;
const MARKER_MID_JITTER_PX = 2;
const MARKER_SAMPLES = 40;
const BRACKET_PRONG_PX = 8;

export type NoteBracketSide = "left" | "right";

function towards(from: SketchPoint, to: SketchPoint, distance: number) {
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  if (length === 0 || distance === 0) return { x: from.x, y: from.y };
  const ratio = Math.min(1, distance / length);
  return {
    x: from.x + (to.x - from.x) * ratio,
    y: from.y + (to.y - from.y) * ratio,
  };
}

function quadraticPoints(
  start: SketchPoint,
  control: SketchPoint,
  end: SketchPoint,
  samples: number,
  includeEnd: boolean,
): SketchPoint[] {
  const points: SketchPoint[] = [];
  const last = includeEnd ? samples : samples - 1;
  for (let index = 0; index <= last; index += 1) {
    const t = index / samples;
    const u = 1 - t;
    points.push({
      x: u * u * start.x + 2 * u * t * control.x + t * t * end.x,
      y: u * u * start.y + 2 * u * t * control.y + t * t * end.y,
    });
  }
  return points;
}

/** Samples one edge, bowed sideways by `bow` at its middle; the end point is
 * left for the next edge. */
function bowedEdge(
  from: SketchPoint,
  to: SketchPoint,
  bow: number,
): SketchPoint[] {
  const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
  const points: SketchPoint[] = [];
  for (let index = 0; index < BOX_EDGE_SAMPLES; index += 1) {
    const t = index / BOX_EDGE_SAMPLES;
    const offset = Math.sin(t * Math.PI) * bow;
    points.push({
      x: from.x + (to.x - from.x) * t - ((to.y - from.y) / length) * offset,
      y: from.y + (to.y - from.y) * t + ((to.x - from.x) / length) * offset,
    });
  }
  return points;
}

/** One continuous box stroke from the top-left: jittered corners, bowed
 * edges, and an overshoot along the top edge instead of a clean close. */
export function sketchBoxPoints(
  width: number,
  height: number,
  random: RandomSource,
  options: { rounded?: boolean } = {},
): SketchPoint[] {
  const jitter = () =>
    randomBetween(random, -BOX_CORNER_JITTER_PX, BOX_CORNER_JITTER_PX);
  const corners: SketchPoint[] = [
    { x: jitter(), y: jitter() },
    { x: width + jitter(), y: jitter() },
    { x: width + jitter(), y: height + jitter() },
    { x: jitter(), y: height + jitter() },
    { x: jitter(), y: jitter() },
  ];
  corners.push({
    x: BOX_OVERSHOOT_PX,
    y: jitter() - BOX_OVERSHOOT_LIFT_PX,
  });
  const radius = options.rounded
    ? Math.min(ROUNDED_CORNER_PX, width / 3, height / 3)
    : 0;
  const points: SketchPoint[] = [];
  for (let edge = 0; edge < 5; edge += 1) {
    const corner = corners[edge]!;
    const next = corners[edge + 1]!;
    const start = towards(corner, next, radius);
    const end = edge < 4 ? towards(next, corner, radius) : next;
    const bow = randomBetween(random, -BOX_EDGE_BOW_PX, BOX_EDGE_BOW_PX);
    points.push(...bowedEdge(start, end, bow));
    if (edge < 4 && radius > 0) {
      const following = corners[edge + 2]!;
      points.push(
        ...quadraticPoints(
          end,
          next,
          towards(next, following, radius),
          ROUNDED_CORNER_SAMPLES,
          false,
        ),
      );
    }
  }
  points.push(corners[5]!);
  return points;
}

/** An open, overshooting loop: starts upper-left, sweeps 1.12 turns with the
 * radius drifting outward 7 % so the end passes its start instead of closing. */
export function sketchLoopPoints(
  width: number,
  height: number,
  random: RandomSource,
): SketchPoint[] {
  const centerX = width / 2;
  const centerY = height / 2;
  const radiusX = width / 2;
  const radiusY = height / 2;
  const startAngle = -2.5 + randomBetween(random, -0.2, 0.2);
  const rotation = randomBetween(random, -0.08, 0.08);
  const phase = randomBetween(random, 0, Math.PI * 2);
  const points: SketchPoint[] = [];
  for (let index = 0; index <= LOOP_SAMPLES; index += 1) {
    const t = index / LOOP_SAMPLES;
    const angle = startAngle + Math.PI * 2 * LOOP_TURNS * t;
    const growth = 1 + 0.04 * Math.sin(2 * angle + phase) + 0.07 * t;
    const x = Math.cos(angle) * radiusX * growth;
    const y = Math.sin(angle) * radiusY * growth;
    points.push({
      x: centerX + x * Math.cos(rotation) - y * Math.sin(rotation),
      y: centerY + x * Math.sin(rotation) + y * Math.cos(rotation),
    });
  }
  return points;
}

/** Two separate head flicks at ±0.5 rad off the shaft's final direction: the
 * first lands on the tip, the second leaves it (pen lifts in between). */
export function arrowHeadStrokes(
  shaft: readonly SketchPoint[],
): [SketchPoint[], SketchPoint[]] {
  const tip = shaft[shaft.length - 1] ?? { x: 0, y: 0 };
  let back = tip;
  let traveled = 0;
  for (let index = shaft.length - 2; index >= 0; index -= 1) {
    const point = shaft[index]!;
    traveled += Math.hypot(back.x - point.x, back.y - point.y);
    back = point;
    if (traveled >= HEAD_LENGTH_PX) break;
  }
  const direction = Math.atan2(tip.y - back.y, tip.x - back.x);
  const flick = (offset: number): SketchPoint[] => [
    {
      x: tip.x - Math.cos(direction + offset) * HEAD_LENGTH_PX,
      y: tip.y - Math.sin(direction + offset) * HEAD_LENGTH_PX,
    },
    { x: tip.x, y: tip.y },
  ];
  return [flick(HEAD_ANGLE_RAD), flick(-HEAD_ANGLE_RAD).reverse()];
}

/** An underline along y = 0 that sags 3–6 px mid-way and lifts 3 px at the
 * end. */
export function sketchUnderlinePoints(
  width: number,
  random: RandomSource,
): SketchPoint[] {
  return quadraticPoints(
    { x: 0, y: 1 },
    { x: width / 2, y: randomBetween(random, 3, 6) },
    { x: width, y: -3 },
    UNDERLINE_SAMPLES,
    true,
  );
}

/** A highlighter pass across the vertical centre of a w × h box, from 2 px
 * before its left edge to 2 px past its right edge. */
export function sketchMarkerPoints(
  width: number,
  height: number,
  random: RandomSource,
): SketchPoint[] {
  const centerY = height / 2;
  const jitter = (reach: number) =>
    centerY + randomBetween(random, -reach, reach);
  return quadraticPoints(
    { x: -MARKER_REACH_PX, y: jitter(MARKER_END_JITTER_PX) },
    { x: width / 2, y: jitter(MARKER_MID_JITTER_PX) },
    { x: width + MARKER_REACH_PX, y: jitter(MARKER_END_JITTER_PX) },
    MARKER_SAMPLES,
    true,
  );
}

/** A tall bracket whose prongs point toward the mark. `side` is where the
 * note sits relative to the mark; the bracket box is 9 px wide. */
export function noteBracketPoints(
  height: number,
  side: NoteBracketSide,
): SketchPoint[] {
  const spine = BRACKET_PRONG_PX;
  const points: SketchPoint[] = [
    { x: 0, y: 2 },
    { x: spine, y: 0 },
    { x: spine + 1, y: height / 2 },
    { x: spine, y: height - 4 },
    { x: 0, y: height - 2 },
  ];
  return side === "right"
    ? points
    : points.map((point) => ({ x: spine + 1 - point.x, y: point.y }));
}
